// SAM-1321: an estate group is a business of the Estate category. The business administers it when the group's
// `admin_business_id` is that business, and the only way the admin knows it is to ask after login: `GET /estate_group`
// with `admin_business_id=<B>` (no claim and no group for it). Nothing the browser decides is security: the server
// narrows every read and write to the group's estates; this only decides what the UI SHOWS.
import { useEffect, useState } from 'react';
import { defaultInstance } from '../api/httpClient';

export interface AdministeredEstateGroup {
  id: number;
  name: string;
}

const inflight = new Map<string, Promise<AdministeredEstateGroup[]>>();

/** One request per business per page load, however many components ask. A failed read is «administers none». */
export const fetchAdministeredEstateGroups = (businessId: string): Promise<AdministeredEstateGroup[]> => {
  const known = inflight.get(businessId);
  if (known) return known;
  const request = defaultInstance({
    method: 'GET',
    url: `/estate_group?admin_business_id=${encodeURIComponent(businessId)}&limit=50`,
    // a business that administers nothing may be answered 403/404: that is an answer, not an error to toast
    quietErrors: true,
  } as Parameters<typeof defaultInstance>[0])
    .then((response) => {
      const body = response as unknown as { data?: unknown } | unknown[];
      const rows = Array.isArray(body) ? body : Array.isArray(body?.data) ? body.data : [];
      return (rows as Array<{ id?: number; name?: string; admin_business_id?: string | null }>)
        // the server filters; this keeps the answer honest if it ever returns the unfiltered list
        .filter((g) => typeof g.id === 'number' && String(g.admin_business_id) === businessId)
        .map((g) => ({ id: g.id as number, name: g.name ?? '' }));
    })
    .catch(() => [] as AdministeredEstateGroup[]);
  inflight.set(businessId, request);
  return request;
};

/** Test hook: forget what was asked. */
export const resetAdministeredEstateGroups = (): void => inflight.clear();

/** The estate groups the business administers; `loading` until the answer comes (or at once when `enabled` is false). */
export const useAdministeredEstateGroups = (businessId: string | null, enabled = true) => {
  const active = enabled && !!businessId;
  const [state, setState] = useState<{ groups: AdministeredEstateGroup[]; loading: boolean }>({ groups: [], loading: active });
  useEffect(() => {
    if (!active) {
      setState({ groups: [], loading: false });
      return undefined;
    }
    let cancelled = false;
    setState((prev) => ({ ...prev, loading: true }));
    fetchAdministeredEstateGroups(businessId as string).then((groups) => {
      if (!cancelled) setState({ groups, loading: false });
    });
    return () => { cancelled = true; };
  }, [active, businessId]);
  return state;
};
